import React, { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { ModalConfirmDialog } from "components";
import { DataTypeEnum } from "store/reducers/products/properties/pl-properties.types";

interface BlanketPriceChangeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    selectedPropertyId: string,
    percent: number,
    roundDigits: number,
    increaseDeductPrice: boolean
  ) => void;
  propertyOptions: { value: string; label: string; dataType: DataTypeEnum }[];
}

const BlanketPriceChangeDialog = ({
  open,
  onClose,
  onSubmit,
  propertyOptions,
}: BlanketPriceChangeDialogProps) => {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const [percent, setPercent] = useState<number>(0.0);
  const [percentInput, setPercentInput] = useState<string>("0");
  const [roundDigits, setRoundDigits] = useState<number>(0);
  const [increaseDeductPrice, setIncreaseDeductPrice] = useState(false);
  const [enableRoundDigits, setEnableRoundDigits] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset form values when dialog is opened
      setSelectedPropertyId("");
      setPercent(0);
      setEnableRoundDigits(false);
      setRoundDigits(0);
      setIncreaseDeductPrice(false);
    }
  }, [open]);

  useEffect(() => {
    if (selectedPropertyId) {
      const dataType: DataTypeEnum | undefined = propertyOptions.find(
        (prop) => {
          return prop.value === selectedPropertyId;
        }
      )?.dataType;
      if (dataType && dataType === DataTypeEnum.FLOAT)
        setEnableRoundDigits(true);
      else setEnableRoundDigits(false);
    } else {
      setEnableRoundDigits(false);
    }
  }, [selectedPropertyId]);

  useEffect(() => {
    //If round digits becomes disabled, set round digits back to 0
    if (!enableRoundDigits) setRoundDigits(0);
  }, [enableRoundDigits]);

  const handlePercentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPercentInput(value);

    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      setPercent(parsed);
    }
  };

  const handlePercentBlur = () => {
    const parsed = parseFloat(percentInput);
    const clamped = isNaN(parsed) ? 0 : Math.min(1000, Math.max(-1000, parsed));

    setPercent(clamped);
    setPercentInput(clamped.toString());
  };

  const handleRoundDigitChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 9) {
      setRoundDigits(parsed);
    } else if (value === "") {
      setRoundDigits(0);
    }
  };

  const handleOK = () => {
    //Pass the inputs from the dialog to the passed handling method to do the price increase.
    onSubmit(selectedPropertyId, percent, roundDigits, increaseDeductPrice);
  };

  const okButtonDisabled =
    !selectedPropertyId || percent === 0 || roundDigits < 0 || roundDigits > 9;

  return (
    <ModalConfirmDialog
      open={open}
      title="Blanket Price Change"
      forceOnTop
      content={
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          width="100%"
          minWidth="300px"
        >
          {/* Numeric Property Picker */}
          <Tooltip
            title={
              <span style={{ whiteSpace: "pre-line" }}>
                {
                  "Select the property column to apply the blanket price to.Only INTEGER or FLOAT properties with OPEN or RANGE domains will be shown."
                }
              </span>
            }
            arrow
            placement="top"
          >
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="property-select-label">Property</InputLabel>
              <Select
                variant="outlined"
                labelId="property-select-label"
                value={selectedPropertyId}
                label="Property"
                onChange={(e) => setSelectedPropertyId(e.target.value)}
              >
                {propertyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>

          {/* Percent Input */}
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip
              title={
                <span style={{ whiteSpace: "pre-line" }}>
                  {
                    "Enter the percentage amount to apply. For example, 15 means a 15% adjustment.\n\nA positive percentage will increase a positive base price and will decrease a negative base price (make it more negative).\n\nA negative percentage will decrease a positive base price and will increase a negative base price (make it less negative)."
                  }
                </span>
              }
              arrow
              placement="top"
            >
              <TextField
                label="Percentage"
                type="number"
                value={percentInput}
                onChange={handlePercentChange}
                onBlur={handlePercentBlur}
                inputProps={{
                  step: "any",
                  min: "-1000",
                  max: "1000",
                  inputMode: "decimal",
                }}
                sx={{ width: 150 }}
              />
            </Tooltip>
            <Box component="span" sx={{ fontSize: 16, fontWeight: 500 }}>
              %
            </Box>
          </Box>

          {/* Round Digits Input */}
          <Tooltip
            title={
              <span style={{ whiteSpace: "pre-line" }}>
                {
                  "The number of decimal places to round the price.\n\nIf the price is an integer property, then it will always round to the nearest integer."
                }
              </span>
            }
            arrow
            placement="top"
          >
            <TextField
              label="Round Digits"
              value={roundDigits}
              type="number"
              inputProps={{ min: 0, max: 9, step: 1 }}
              onChange={handleRoundDigitChange}
              disabled={!enableRoundDigits}
              sx={{ width: 150 }}
            />
          </Tooltip>

          {/* Checkbox with Tooltip */}
          <Tooltip
            title={
              <span style={{ whiteSpace: "pre-line" }}>
                {
                  "If checked, a negative base price will become less negative with a positive percentage value.\n\nA negative base price will become more negative with a negative percentage value.\n\nThis will have no effect on positive prices."
                }
              </span>
            }
            arrow
            placement="top"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={increaseDeductPrice}
                  onChange={(e) => setIncreaseDeductPrice(e.target.checked)}
                />
              }
              label="Increase Deduct Price"
            />
          </Tooltip>
        </Box>
      }
      onCancelButtonClick={onClose}
      onOKButtonClick={handleOK}
      okButtonText="Apply"
      okButtonDisabled={okButtonDisabled}
    />
  );
};

export default BlanketPriceChangeDialog;
